import { useCallback, useEffect, useRef, useState } from 'react'
import { cellId } from './utils'

/**
 * Manages which cells are selected on the schedule grid.
 * Drag-select uses react-selectable-fast; click-toggle is handled manually.
 *
 * @param {string[]} rowIds   ordered row identifiers
 * @param {string[]} dayKeys  ordered day keys (yyyy-MM-dd)
 */
export function useGridSelection(rowIds, dayKeys) {
  const [selected, setSelected] = useState(() => new Set())
  const groupRef    = useRef(null)
  const [clearingKey, setClearingKey] = useState(0)

  // Drop stale selections when the visible rows / days change.
  useEffect(() => {
    setSelected((prev) => {
      const rowSet = new Set(rowIds)
      const daySet = new Set(dayKeys)
      const valid  = new Set()
      prev.forEach((k) => {
        const [r, d] = k.split('|')
        if (rowSet.has(r) && daySet.has(d)) valid.add(k)
      })
      return valid.size === prev.size ? prev : valid
    })
  }, [rowIds, dayKeys])

  /**
   * Called by SelectableGroup onSelectionFinish.
   * Single-cell clicks are handled by toggleCell on the <td> onClick instead,
   * so we skip callbacks with only 1 item here to avoid double-toggling.
   */
  const onSelectionFinish = useCallback((selectedItems) => {
    const keys = selectedItems.map((item) => item.props.selectableKey)
    if (keys.length <= 1) return
    setSelected((prev) => {
      const next = new Set(prev)
      keys.forEach((k) => next.add(k))
      return next
    })
  }, [])

  const toggleCell = useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const selectRow = useCallback(
    (rowIndex) => {
      const keys = dayKeys.map((d) => cellId(rowIds[rowIndex], d))
      const allSelected = keys.every((k) => selected.has(k))
      setSelected((prev) => {
        const next = new Set(prev)
        if (allSelected) keys.forEach((k) => next.delete(k))
        else keys.forEach((k) => next.add(k))
        return next
      })
    },
    [rowIds, dayKeys, selected],
  )

  const selectColumn = useCallback(
    (colIndex) => {
      const keys = rowIds.map((r) => cellId(r, dayKeys[colIndex]))
      const allSelected = keys.every((k) => selected.has(k))
      setSelected((prev) => {
        const next = new Set(prev)
        if (allSelected) keys.forEach((k) => next.delete(k))
        else keys.forEach((k) => next.add(k))
        return next
      })
    },
    [rowIds, dayKeys, selected],
  )

  const clear = useCallback(() => {
    setSelected(new Set())
    if (groupRef.current?.clearSelection) groupRef.current.clearSelection()
    setClearingKey((k) => k + 1)
  }, [])

  const isCellSelected = useCallback((key) => selected.has(key), [selected])

  return {
    selected,
    setSelected,
    groupRef,
    clearingKey,
    onSelectionFinish,
    toggleCell,
    selectRow,
    selectColumn,
    clear,
    isCellSelected,
  }
}
